import React, { memo, useMemo } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { createUseStyles } from '@Sage/hoi-poi-ui';
import { CSSTransition } from 'react-transition-group';

import styles from './styles';
const useStyles = createUseStyles(styles, 'Transition');

const Transition = ({
    children,
    duration = '0.3s',
    overflow,
    show,
    transitionKey,
    transition,
    timeout,
    className,
}) => {
    const classes = useStyles();

    const transitionClass = useMemo(() => {
        switch (transition) {
            case 'slideDown':
                return 'slideDownTransition';
            case 'slideUp':
                return 'slideUpTransition';
            case 'fade':
            default:
                return 'fadeTransition';
        }
    }, [transition]);

    const transitionProps = useMemo(() => {
        const classNames = classnames(
            {
                [classes.overflow]: overflow,
            },
            className,
            transitionClass,
        );

        // Parse duration string (e.g., "2s" -> 2000, "300ms" -> 300)
        const parseDuration = (durationStr) => {
            if (!durationStr) return null;
            const match = durationStr.match(/^(\d+(?:\.\d+)?)(ms|s)$/);
            if (!match) return null;
            const value = parseFloat(match[1]);
            const unit = match[2];
            return unit === 's' ? value * 1000 : value;
        };

        const durationMs = parseDuration(duration);

        return {
            key: transitionKey,
            in: !!show,
            classNames,
            unmountOnExit: true,
            timeout:
                timeout ||
                (durationMs
                    ? {
                          enter: durationMs,
                          exit: durationMs,
                      }
                    : {
                          enter: 200,
                          exit: 0,
                      }),
        };
    }, [
        classes.overflow,
        overflow,
        className,
        transitionClass,
        transitionKey,
        show,
        timeout,
        duration,
    ]);

    const wrapperProps = useMemo(() => {
        const style = {};
        if (duration) {
            style['--transition-duration'] = duration;
        }
        return {
            className: classes[transitionClass],
            style,
        };
    }, [duration, classes, transitionClass]);

    return (
        <CSSTransition {...transitionProps}>
            <div {...wrapperProps}>{children}</div>
        </CSSTransition>
    );
};

Transition.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    duration: PropTypes.string,
    overflow: PropTypes.bool,
    show: PropTypes.any,
    timeout: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({
            enter: PropTypes.number,
            exit: PropTypes.number,
        }),
    ]),
    transition: PropTypes.oneOf(['fade', 'slideDown', 'slideUp']),
    transitionKey: PropTypes.string,
};

export default memo(Transition);
